import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { setAccessToken } from '@/utils/tokenStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Hotel, Loader2, Globe, Sparkles, ArrowRight, Shield, Smartphone, HelpCircle, Mail, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useFeatureGating } from '@/contexts/FeatureGatingContext';
import { getErrorMessage } from '@/utils/errorUtils';
import LoginWalkthrough from '@/components/LoginWalkthrough';
import MobileOtpLogin from '@/components/auth/MobileOtpLogin';

export default function Login() {
  const { t, language, setLanguage, isEnglish } = useLanguage();
  const { refreshFeatureAccess } = useFeatureGating();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  
  // Login mode: 'email' or 'mobile'
  const [loginMode, setLoginMode] = useState('email');
  
  // MFA State
  const [showMfaInput, setShowMfaInput] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    // Ensure walkthrough won't pop up during/after login
    localStorage.setItem('merina_walkthrough_seen', 'true');
    setShowWalkthrough(false);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      // Check if MFA is required
      if (response.data.mfa_required) {
        setMfaToken(response.data.mfa_token);
        setShowMfaInput(true);
        toast.info(t('auth.enterAuthenticatorCode'));
        setLoading(false);
        return;
      }
      
      // Normal login without MFA
      await completeLogin(response.data);
    } catch (error) {
      const msg = getErrorMessage(error, t('auth.loginFailed'));
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    if (!mfaCode || mfaCode.length !== 6) {
      toast.error(t('auth.enter6DigitCode'));
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login/mfa`, {
        mfa_token: mfaToken,
        code: mfaCode
      });
      await completeLogin(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, t('auth.invalidMfaCode')));
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = async (data) => {
    // Store access_token in memory for Safari/iOS fallback (ITP blocks cookies)
    if (data.access_token) {
      setAccessToken(data.access_token);
    }
    
    // Only store non-sensitive user info for UI display (NOT the JWT token).
    // Auth is handled entirely via httpOnly cookies set by the backend.
    localStorage.setItem('user', JSON.stringify(data.user));
    
    // Store permissions from login response (includes custom DB permissions)
    if (data.permissions) {
      localStorage.setItem('user_permissions', JSON.stringify(data.permissions));
    }
    
    // Store hotel context for multi-hotel owners
    if (data.hotel_context) {
      localStorage.setItem('hotel_context', JSON.stringify(data.hotel_context));
    }
    
    // Store demo status if applicable
    if (data.is_demo) {
      localStorage.setItem('is_demo', 'true');
      localStorage.setItem('demo_status', JSON.stringify(data.demo_status || {}));
    } else {
      localStorage.removeItem('is_demo');
      localStorage.removeItem('demo_status');
    }
    
    // Fetch permissions BEFORE navigation so pages render correctly on first load
    try {
      const permRes = await fetch(`${API}/auth/permissions`, { credentials: 'include' });
      if (permRes.ok) {
        const permData = await permRes.json();
        localStorage.setItem('user_permissions', JSON.stringify(permData.combined_permissions || []));
      }
    } catch (e) {
      // Non-blocking - will fall back to static defaults
    }
    
    toast.success(t('auth.loginSuccess'));
    navigate('/');
    
    // Refresh feature gating context after navigation
    setTimeout(() => refreshFeatureAccess(), 100);
  };

  const handleBackToLogin = () => {
    setShowMfaInput(false);
    setMfaCode('');
    setMfaToken('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Language Selector - Top Right Corner */}
      <div className="absolute top-4 right-4">
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="bg-white/90 hover:bg-white px-3 py-2 rounded-md border shadow-lg text-sm font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500"
          data-testid="language-selector"
        >
          <option value="en">🇬🇧 English</option>
          <option value="ta">🇮🇳 தமிழ்</option>
          <option value="hi">🇮🇳 हिन्दी</option>
          <option value="kn">🇮🇳 ಕನ್ನಡ</option>
          <option value="te">🇮🇳 తెలుగు</option>
          <option value="ml">🇮🇳 മലയാളം</option>
        </select>
      </div>

      <Card className="w-full max-w-md shadow-2xl" data-testid="login-card">
        <CardHeader className="text-center space-y-4">
          {/* Clickable Logo - Goes to Landing Page */}
          <Link to="/" className="flex justify-center group" data-testid="logo-home-link" aria-label="Go to Merina HMS home page">
            <img src="/merina-logo-md.png" alt="Merina HMS - Hotel Management Made Simple" className="h-16 sm:h-20 w-auto object-contain group-hover:scale-105 transition-transform" />
          </Link>
          <CardDescription>{t('auth.loginSubtitle')}</CardDescription>
          
          {/* Try Free Trial Banner - Fully Clickable */}
          <div 
            onClick={() => navigate('/register')}
            className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-lg p-3 cursor-pointer hover:shadow-md hover:border-green-300 transition-all"
            data-testid="free-trial-banner"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">{t('auth.tryFreeTrial')}</span>
              </div>
              <Badge className="bg-green-100 text-green-700 text-xs">14 {t('auth.daysLabel')}</Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-2 border-green-300 text-green-700 hover:bg-green-100"
              data-testid="try-trial-button"
            >
              {t('auth.startFreeTrial')}
              <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loginMode === 'mobile' ? (
            // Mobile OTP Login
            <MobileOtpLogin 
              onBackToEmail={() => setLoginMode('email')}
              onSuccess={(data) => {
                toast.success(t('auth.loginSuccess'));
                navigate('/');
              }}
            />
          ) : !showMfaInput ? (
            // Normal Login Form
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Error Message Display */}
              {errorMessage && (
                <div 
                  className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2"
                  role="alert"
                  data-testid="login-error"
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{errorMessage}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrorMessage('');
                  }}
                  required
                  data-testid="email-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrorMessage('');
                    }}
                    required
                    data-testid="password-input"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    data-testid="toggle-password-btn"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <a href="/forgot-password" className="text-sm text-purple-600 hover:text-purple-700 hover:underline">
                  {t('auth.forgotPassword')}
                </a>
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.loggingIn')}
                  </>
                ) : (
                  t('auth.loginButton')
                )}
              </Button>
              
              {/* Mobile OTP Login Option - Disabled until DLT template IDs are configured */}
              {/* Backend OTP logic preserved in MobileOtpLogin component for future use */}
            </form>
          ) : (
            // MFA Verification Form
            <form onSubmit={handleMfaVerify} className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                <Smartphone className="w-10 h-10 text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-purple-800">{t('auth.mfa.title')}</p>
                <p className="text-xs text-purple-600 mt-1">{t('auth.mfa.subtitle')}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="mfa-code">{t('auth.mfa.codeLabel')}</Label>
                <Input
                  id="mfa-code"
                  type="text"
                  maxLength={6}
                  placeholder={t('auth.mfa.codePlaceholder')}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  required
                  autoFocus
                  data-testid="mfa-code-input"
                  className="h-14 text-center text-2xl tracking-widest font-mono"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full h-11 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                disabled={loading || mfaCode.length !== 6}
                data-testid="verify-mfa-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.mfa.verifying')}
                  </>
                ) : (
                  <>
                    <Shield className="mr-2 h-4 w-4" />
                    {t('auth.mfa.verifyAndLogin')}
                  </>
                )}
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={handleBackToLogin}
              >
                ← {t('auth.mfa.backToLogin')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Help Button - Shows Walkthrough */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowWalkthrough(true)}
        className="fixed bottom-6 left-6 shadow-lg hover:shadow-xl transition-shadow bg-white z-40"
        aria-label="Show walkthrough tutorial"
        data-testid="show-walkthrough-btn"
      >
        <HelpCircle className="w-4 h-4 mr-2" />
        {t('auth.needHelp')}
      </Button>

      {/* Walkthrough Tutorial Modal */}
      <LoginWalkthrough 
        isOpen={showWalkthrough} 
        onOpenChange={setShowWalkthrough}
      />
      
      {/* Auto-show walkthrough for first-time visitors - only if form not interacted with */}
      {!email && !password && !loading && <LoginWalkthrough />}
    </div>
  );
}